/**
 * Volume, fades, trim and replace/remove for the music track. Lives outside
 * the timeline's horizontal scroller so it is never clipped.
 *
 * Sliders write live without recording and checkpoint once at gesture start,
 * so one drag is one timeline undo step.
 */

import { useRef } from 'react';
import { formatTimecode } from '@/lib/video/sceneTimeline';
import { useVideoStore } from '@/stores/videoStore';
import AudioImportButton from './AudioImportButton';

const MAX_FADE_MS = 5000;

interface AudioTrackControlsProps {
  onClose: () => void;
}

export default function AudioTrackControls({ onClose }: AudioTrackControlsProps) {
  const track = useVideoStore((s) => s.video?.audio ?? null);
  const { checkpoint, updateAudio, setAudio } = useVideoStore.getState();
  const gestureRef = useRef(false);

  if (!track) return null;

  const begin = () => {
    if (gestureRef.current) return;
    gestureRef.current = true;
    checkpoint();
  };
  const end = () => {
    gestureRef.current = false;
  };
  const slider = (args: {
    label: string;
    value: number;
    min: number;
    max: number;
    step: number;
    display: string;
    onChange: (v: number) => void;
  }) => (
    <label className="vt-audio-field">
      <span>{args.label}</span>
      <input
        type="range"
        min={args.min}
        max={args.max}
        step={args.step}
        value={args.value}
        onPointerDown={begin}
        onPointerUp={end}
        onKeyDown={begin}
        onKeyUp={end}
        onChange={(e) => args.onChange(Number(e.target.value))}
      />
      <output>{args.display}</output>
    </label>
  );

  return (
    <div className="vt-audio-controls" role="group" aria-label="Music settings">
      <strong className="vt-audio-title" title={track.name}>
        ♪ {track.name}
      </strong>
      {slider({
        label: 'Volume',
        value: track.volume,
        min: 0,
        max: 1,
        step: 0.01,
        display: `${Math.round(track.volume * 100)}%`,
        onChange: (volume) => updateAudio({ volume }, { record: false }),
      })}
      {slider({
        label: 'Fade in',
        value: track.fade.inMs,
        min: 0,
        max: MAX_FADE_MS,
        step: 100,
        display: `${(track.fade.inMs / 1000).toFixed(1)}s`,
        onChange: (inMs) => updateAudio({ fade: { ...track.fade, inMs } }, { record: false }),
      })}
      {slider({
        label: 'Fade out',
        value: track.fade.outMs,
        min: 0,
        max: MAX_FADE_MS,
        step: 100,
        display: `${(track.fade.outMs / 1000).toFixed(1)}s`,
        onChange: (outMs) => updateAudio({ fade: { ...track.fade, outMs } }, { record: false }),
      })}
      {slider({
        label: 'Start at',
        value: track.trimStartMs,
        min: 0,
        max: Math.max(0, track.durationMs - 1000),
        step: 100,
        display: formatTimecode(track.trimStartMs),
        onChange: (trimStartMs) => updateAudio({ trimStartMs }, { record: false }),
      })}
      <span className="vt-toolbar-spacer" />
      <AudioImportButton label="Replace" />
      <button
        type="button"
        className="vt-btn"
        onClick={() => {
          setAudio(null);
          onClose();
        }}
      >
        Remove
      </button>
      <button type="button" className="vt-btn" onClick={onClose} aria-label="Close music settings">
        ✕
      </button>
    </div>
  );
}
